<template>
  <v-dialog persistent v-model="show" max-width="770px">
    <v-card>
      <v-btn icon class="btn-cancelar-flotante" @click="cancel">
        <v-icon>mdi-close</v-icon>
      </v-btn>

      <v-card-title>
        <div class="headline">Solicitar Vacaciones</div>
      </v-card-title>

      <hr class="header-separator" />

      <v-card-text>
        <v-container>
          <v-row dense>
            <v-col cols="12">
              <div class="campo-label">Motivo <span class="required-asterisk">*</span></div>
              <v-text-field
                v-model="form.reason"
                placeholder="Escriba el motivo de sus vacaciones"
                class="campo-redondeado"
                :class="{ 'campo-error': validationErrors.reason }"
                solo
                :error-messages="validationErrors.reason ? [validationErrors.reason] : []"
                required
              ></v-text-field>
            </v-col>

            <v-col cols="6">
              <div class="campo-label">Día de Inicio <span class="required-asterisk">*</span></div>
              <v-menu
                v-model="startDatePickerMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.startDate"
                    placeholder="DD/MM/YYYY"
                    class="campo-redondeado"
                    :class="{ 'campo-error': validationErrors.startDate }"
                    solo
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="validationErrors.startDate ? [validationErrors.startDate] : []"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="startDatePickerValue"
                  @input="onStartDateSelected"
                  :first-day-of-week="1"
                  locale="es"
                  no-title
                  scrollable
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="6">
              <div class="campo-label">Día de Fin <span class="required-asterisk">*</span></div>
              <v-menu
                v-model="endDatePickerMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.endDate"
                    placeholder="DD/MM/YYYY"
                    class="campo-redondeado"
                    :class="{ 'campo-error': validationErrors.endDate }"
                    solo
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    prepend-inner-icon="mdi-calendar"
                    :error-messages="validationErrors.endDate ? [validationErrors.endDate] : []"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="endDatePickerValue"
                  @input="onEndDateSelected"
                  :first-day-of-week="1"
                  locale="es"
                  no-title
                  scrollable
                  :min="startDatePickerValue"
                ></v-date-picker>
              </v-menu>
            </v-col>

            <v-col cols="12">
              <v-checkbox
                v-model="form.sendEmailNotification"
                class="mt-0"
                hide-details
              >
                <template v-slot:label>
                  <span class="checkbox-label">
                    Enviar notificación por email al administrador
                  </span>
                </template>
              </v-checkbox>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions class="justify-center">
        <v-btn class="btn-fondo" @click="save">Guardar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dialog de error -->
    <v-dialog v-model="errorDialog.show" max-width="500px">
      <v-card>
        <v-card-title class="error-dialog-header">
          <v-icon color="white" large left>mdi-alert-circle</v-icon>
          <span class="error-dialog-title">Error al solicitar vacaciones</span>
        </v-card-title>

        <v-card-text class="error-dialog-content">
          <div class="error-icon-wrapper">
            <v-icon color="error" size="60">mdi-alert-circle-outline</v-icon>
          </div>
          <div class="error-message">
            {{ errorDialog.message }}
          </div>
          <div v-if="errorDialog.details" class="error-details">
            {{ errorDialog.details }}
          </div>
        </v-card-text>

        <v-card-actions class="error-dialog-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="errorDialog.show = false">
            Entendido
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar para errores -->
    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      :timeout="snackbar.timeout"
    >
      <b>{{ snackbar.text }}</b>
    </v-snackbar>
  </v-dialog>
</template>

<script>
import { Absence } from "../domain/Absence";
import { v4 as uuidv4 } from "uuid";

export default {
  name: "UpdateHolidayPopup",
  props: {
    show: Boolean,
    holiday: Object,
    preselectedDate: Date,
    existingHolidays: {
      type: Array,
      default: () => []
    },
    errors: {
      type: Object,
      default: () => ({})
    },
    companydbId: {
      type: [Number, String],
      default: null
    }
  },
  data() {
    return {
      form: {
        reason: "",
        startDate: "",
        endDate: "",
        sendEmailNotification: true
      },
      startDatePickerMenu: false,
      endDatePickerMenu: false,
      startDatePickerValue: null,
      endDatePickerValue: null,
      errorDialog: {
        show: false,
        message: '',
        details: ''
      },
      validationErrors: {
        reason: '',
        startDate: '',
        endDate: ''
      },
      snackbar: {
        visible: false,
        text: '',
        color: 'error',
        timeout: 3000
      }
    };
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeForm();
      }
    },
    preselectedDate() {
      if (this.show) {
        this.initializeForm();
      }
    }
  },
  mounted() {
    this.initializeForm();
  },
  computed: {
    companydb_id() {
      if (this.companydbId) {
        return parseInt(this.companydbId);
      }
      if (this.$route.params.companydb_id) {
        return parseInt(this.$route.params.companydb_id);
      }
      const userData = this.$store.getters['me/userData'];
      if (userData && userData.company_id) {
        return parseInt(userData.company_id);
      }
      return null;
    }
  },
  methods: {
    initializeForm() {
      const dateToUse = this.getInitialDate();

      function pad(n) {
        return n < 10 ? "0" + n : n;
      }

      this.form = {
        reason: (this.holiday && this.holiday.reason) ? this.holiday.reason : "",
        startDate: this.holiday ?
          this.formatDateFromDate(new Date(this.holiday.start)) :
          pad(dateToUse.getDate()) + "/" + pad(dateToUse.getMonth() + 1) + "/" + dateToUse.getFullYear(),
        endDate: this.holiday ?
          this.formatDateFromDate(new Date(this.holiday.end)) :
          pad(dateToUse.getDate()) + "/" + pad(dateToUse.getMonth() + 1) + "/" + dateToUse.getFullYear(),
        sendEmailNotification: true
      };

      // Establecer datePickerValue en formato YYYY-MM-DD para el date picker
      const year = dateToUse.getFullYear();
      const month = pad(dateToUse.getMonth() + 1);
      const day = pad(dateToUse.getDate());
      this.startDatePickerValue = year + '-' + month + '-' + day;
      this.endDatePickerValue = year + '-' + month + '-' + day;

      // Limpiar errores de validación al inicializar
      this.validationErrors = {
        reason: '',
        startDate: '',
        endDate: ''
      };
    },

    getInitialDate() {
      if (this.preselectedDate) {
        return new Date(this.preselectedDate);
      }
      if (this.holiday) {
        return new Date(this.holiday.start);
      }
      return new Date();
    },

    formatDateFromDate(date) {
      function pad(n) {
        return n < 10 ? "0" + n : n;
      }
      return pad(date.getDate()) + "/" + pad(date.getMonth() + 1) + "/" + date.getFullYear();
    },

    onStartDateSelected(date) {
      // date viene en formato YYYY-MM-DD del date picker
      // Convertir a DD/MM/YYYY
      const parts = date.split('-');
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      this.form.startDate = day + '/' + month + '/' + year;
      this.startDatePickerMenu = false;

      // Limpiar error de validación si existe
      if (this.validationErrors.startDate) {
        this.validationErrors.startDate = '';
      }

      // Actualizar la fecha mínima para la fecha de fin
      if (this.endDatePickerValue && this.endDatePickerValue < date) {
        this.endDatePickerValue = date;
        this.form.endDate = this.form.startDate;
      }
    },

    onEndDateSelected(date) {
      // date viene en formato YYYY-MM-DD del date picker
      // Convertir a DD/MM/YYYY
      const parts = date.split('-');
      const year = parts[0];
      const month = parts[1];
      const day = parts[2];
      this.form.endDate = day + '/' + month + '/' + year;
      this.endDatePickerMenu = false;

      // Limpiar error de validación si existe
      if (this.validationErrors.endDate) {
        this.validationErrors.endDate = '';
      }
    },

    showSnackbar(color, text) {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.visible = true;
    },

    cancel() {
      this.$emit("close", null);
    },

    async save() {
      // Limpiar errores previos
      this.validationErrors = {
        reason: '',
        startDate: '',
        endDate: ''
      };

      let hasErrors = false;

      // Validar motivo
      if (!this.form.reason || !this.form.reason.trim()) {
        this.validationErrors.reason = "Debe escribir el motivo de las vacaciones";
        hasErrors = true;
      }

      // Validar fecha inicio
      const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
      if (!this.form.startDate) {
        this.validationErrors.startDate = "Debe seleccionar una fecha de inicio";
        hasErrors = true;
      } else if (!dateRegex.test(this.form.startDate)) {
        this.validationErrors.startDate = "Formato inválido (DD/MM/YYYY)";
        hasErrors = true;
      }

      // Validar fecha fin
      if (!this.form.endDate) {
        this.validationErrors.endDate = "Debe seleccionar una fecha de fin";
        hasErrors = true;
      } else if (!dateRegex.test(this.form.endDate)) {
        this.validationErrors.endDate = "Formato inválido (DD/MM/YYYY)";
        hasErrors = true;
      }

      // Si hay errores, detener
      if (hasErrors) {
        return;
      }

      const startDateParts = this.form.startDate.split("/").map(Number);
      const startDay = startDateParts[0];
      const startMonth = startDateParts[1];
      const startYear = startDateParts[2];

      const endDateParts = this.form.endDate.split("/").map(Number);
      const endDay = endDateParts[0];
      const endMonth = endDateParts[1];
      const endYear = endDateParts[2];

      // Crear fechas en UTC para evitar problemas de zona horaria
      // Usar Date.UTC para crear fechas directamente en UTC
      const startDate = new Date(Date.UTC(startYear, startMonth - 1, startDay, 0, 0, 0));
      const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay, 23, 59, 59));

      if (endDate < startDate) {
        this.validationErrors.startDate = "Verifica las fechas";
        this.validationErrors.endDate = "La fecha fin debe ser mayor o igual que la fecha inicio";
        this.showSnackbar('error', 'Error: fecha inicio no concuerda con fecha fin');
        return;
      }

      // Verificar solapamiento solo con otras VACACIONES (no con otros tipos de ausencias)
      for (const existingHoliday of this.existingHolidays) {
        // Solo validar contra otras vacaciones, ignorar otros tipos de ausencias
        if (existingHoliday.tipoAusencia !== 'Vacaciones') {
          continue;
        }

        const existingStart = new Date(existingHoliday.start);
        const existingEnd = new Date(existingHoliday.end);

        // Verificar si hay solapamiento
        const hasOverlap = !(endDate < existingStart || startDate > existingEnd);

        if (hasOverlap) {
          this.validationErrors.startDate = "Conflicto con otras vacaciones";
          this.validationErrors.endDate = "Conflicto con otras vacaciones";
          this.showSnackbar('error', 'Ya tienes vacaciones solicitadas para esos días');
          return;
        }
      }

      // Crear objeto de vacaciones con tipo automático "Vacaciones"
      const holiday = new Absence(
        uuidv4(),
        startDate.toISOString(),
        endDate.toISOString(),
        this.form.reason.trim(),
        'pending',  // status por defecto
        null,  // sin justificante
        null,  // lista_motivos (no se usa)
        'Vacaciones'  // tipoAusencia = Vacaciones (automático)
      );

      // Agregar la flag de notificación por email
      holiday.sendEmailNotification = this.form.sendEmailNotification;

      this.$emit("close", holiday);
    },

    showErrorDialog(message, details = '') {
      this.errorDialog.message = message;
      this.errorDialog.details = details;
      this.errorDialog.show = true;

      if (message && (message.includes('Conflicto') || message.includes('solapa') || message.includes('existe'))) {
        this.validationErrors.startDate = "Conflicto con otras vacaciones";
        this.validationErrors.endDate = "Conflicto con otras vacaciones";
        this.showSnackbar('error', 'Error: conflicto con otras vacaciones registradas');
      }
    },
  },
};
</script>

<style scoped>
.btn-cancelar-flotante {
  position: absolute;
  right: 10px;
  top: 10px;
}

.header-separator {
  margin: 0 20px 10px;
  border: none;
  border-top: 1px solid #ccc;
}

.campo-label {
  font-weight: 600;
  margin-bottom: 6px;
  color: #333;
}

.required-asterisk {
  color: #d32f2f;
  font-weight: bold;
  margin-left: 2px;
}

.campo-redondeado .v-input__control {
  border-radius: 12px;
  background-color: var(--v-neutral-base);
}

.campo-error .v-input__control {
  border: 2px solid #d32f2f !important;
  background-color: #ffebee !important;
}

.campo-error .v-input__slot {
  border: 2px solid #d32f2f !important;
}

.v-messages.error--text {
  color: #d32f2f !important;
  font-size: 12px;
  margin-top: 4px;
  font-weight: 500;
}

.btn-fondo {
  background: var(--Primary-700, #7339B7)!important;
  color: white;
  text-transform: none;
  font-weight: 600;
  margin-bottom: 30px;
}

.btn-fondo:hover {
  background-color: #7a66d9;
}
</style>

<style>
/* Estilos del dialog de error */
.error-dialog-header {
  background-color: #d32f2f !important;
  color: white !important;
  padding: 20px 24px !important;
}

.error-dialog-title {
  color: white !important;
  font-size: 20px !important;
  font-weight: 600 !important;
}

.error-dialog-content {
  padding: 32px 24px !important;
  text-align: center;
}

.error-icon-wrapper {
  margin-bottom: 20px;
}

.error-message {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
  line-height: 1.5;
}

.error-details {
  font-size: 14px;
  color: #666;
  margin-top: 8px;
  line-height: 1.6;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border-left: 4px solid #d32f2f;
}

.error-dialog-actions {
  padding: 16px 24px !important;
  background-color: var(--v-neutral-base) !important;
}

.checkbox-label {
  color: #333;
  font-size: 14px;
  font-weight: 500;
}
</style>
